import { IBrowserClient } from '@splitsoftware/splitio/types/splitio';
import { useAsyncResource } from 'use-async-resource';
import { useCurrentUserQueryContext } from 'hooks/useCurrentUser/CurrentUserQueryContext';
import { readCentralizedCurrentUser } from 'hooks/useCurrentUser/suspense';
import { getClientWithState } from '../StatefulSplitClient';
import type { StatefulSplitClientState } from '../StatefulSplitClient';
import { logger } from 'modules/logger';
import { useContext, useMemo } from 'react';
import { MockSplitClientContext } from '../useSplitClient';

async function initializeSplitClient(
  email: string,
  mockedClient: IBrowserClient | null
): Promise<IBrowserClient | null> {
  if (mockedClient != null) return mockedClient;

  return new Promise(
    (
      resolve: (result: Promise<null | IBrowserClient> | null | IBrowserClient) => void,
      reject: (error?: any) => void
    ) => {
      const statefulClient = getClientWithState(email);

      if (statefulClient.state === 'ready') {
        logger.info('Suspense Split Client: already ready');
        resolve(statefulClient.splitClient);
      } else if (statefulClient.state === 'timeout') {
        logger.info('Suspense Split Client: already timed out');

        resolve(null);
      } else {
        logger.info('Suspense Split Client: pending');

        const onStateChange = (state: StatefulSplitClientState) => {
          if (state === 'ready') {
            logger.info('Suspense Split Client: ready');
            resolve(statefulClient.splitClient);
          } else if (state === 'timeout') {
            logger.info('Suspense Split Client: timed out');
            /**
             * Instead of rejecting the promise on timeout, we resolve with the
             * client as null to trigger a fallback value for the suspense version.
             */
            resolve(null);
          }
        };

        statefulClient.onStateChange(onStateChange);
      }
    }
  );
}

/**
 * NOTE: uses Suspense, make sure to handle it accordingly
 */
export const useSplitClient = (): IBrowserClient | null => {
  const mockedSplitClient = useContext(MockSplitClientContext);
  const currentUserQueryContext = useCurrentUserQueryContext();
  const email =
    mockedSplitClient == null ? readCentralizedCurrentUser(currentUserQueryContext).me.email : '';

  const [clientReader] = useAsyncResource(
    initializeSplitClient,
    email,
    mockedSplitClient?.splitClient ?? null
  );

  const splitIoClient = clientReader();

  const client = useMemo(
    () => mockedSplitClient?.splitClient ?? splitIoClient,
    [mockedSplitClient?.splitClient, splitIoClient]
  );

  return client;
};
